import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, MapPin, Check, Star, ArrowRight, Info } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useBooking } from "../components/BookingDialog";
import ActivityCard from "../components/ActivityCard";
import Reveal from "../components/Reveal";
import { ACTIVITIES } from "../data/mock";

const ActivityDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const { openBooking } = useBooking();

  const activity = ACTIVITIES.find((a) => a.slug === slug);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveImg(0);
  }, [slug]);

  useEffect(() => {
    if (!activity) navigate("/activites");
  }, [activity, navigate]);

  if (!activity) return null;

  const gallery = activity.gallery && activity.gallery.length ? activity.gallery : [activity.image];
  const others = ACTIVITIES.filter((a) => a.id !== activity.id && a.category === activity.category).slice(0, 3);
  const fallbackOthers = ACTIVITIES.filter((a) => a.id !== activity.id).slice(0, 3);
  const suggestions = others.length ? others : fallbackOthers;

  return (
    <div className="bg-[#f7f5ef] min-h-screen">
      {/* Gallery header */}
      <section className="relative pt-24">
        <div className="relative h-[52vh] md:h-[68vh] overflow-hidden">
          <img src={gallery[activeImg]} alt={activity.title[lang]} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1c221e]/80 via-[#1c221e]/10 to-[#1c221e]/30" />

          <div className="absolute top-5 left-0 right-0">
            <div className="max-w-7xl mx-auto px-5 md:px-8">
              <button
                onClick={() => navigate("/activites")}
                className="inline-flex items-center gap-2 text-white/90 hover:text-white text-sm tracking-wide bg-black/25 backdrop-blur-sm px-4 py-2 rounded-full transition"
              >
                <ArrowLeft size={16} /> {t.detail.back}
              </button>
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 pb-8">
            <div className="max-w-7xl mx-auto px-5 md:px-8">
              {activity.popular && (
                <span className="inline-block bg-[#f7f5ef]/95 text-[#5c6b54] text-[10px] font-semibold tracking-[0.18em] uppercase px-3 py-1.5 rounded-full mb-4">
                  {t.activities.popular}
                </span>
              )}
              <h1 className="font-serif text-4xl md:text-6xl text-white mb-4 max-w-3xl">{activity.title[lang]}</h1>
              <div className="flex flex-wrap items-center gap-5 text-white/90 text-sm">
                <span className="flex items-center gap-2"><MapPin size={15} className="text-[#b6c3ab]" /> {activity.location[lang]}</span>
                <span className="flex items-center gap-2"><Clock size={15} className="text-[#b6c3ab]" /> {activity.duration[lang]}</span>
                <span className="flex items-center gap-1"><Star size={15} className="fill-[#e6c766] text-[#e6c766]" /> 4.9</span>
              </div>
            </div>
          </div>
        </div>

        {gallery.length > 1 && (
          <div className="max-w-7xl mx-auto px-5 md:px-8 -mt-6 relative z-10">
            <div className="flex gap-3">
              {gallery.map((g, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`w-20 h-14 md:w-28 md:h-20 rounded-lg overflow-hidden border-2 transition ${
                    activeImg === i ? "border-[#7c8f74]" : "border-white/60 opacity-70 hover:opacity-100"
                  }`}
                >
                  <img src={g} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Body */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left content */}
          <div className="lg:col-span-2 space-y-12">
            <Reveal>
              <h2 className="font-serif text-3xl text-[#20261f] mb-5">{t.detail.about}</h2>
              <p className="text-[#5a6055] text-lg leading-relaxed">{activity.description[lang]}</p>
            </Reveal>

            {activity.included.length > 0 && (
              <div>
                <h2 className="font-serif text-3xl text-[#20261f] mb-6">{t.detail.included}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {activity.included.map((inc, i) => (
                    <div key={i} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-[#eceae1]">
                      <span className="w-6 h-6 rounded-full bg-[#7c8f74]/15 flex items-center justify-center shrink-0 mt-0.5">
                        <Check size={14} className="text-[#5c6b54]" />
                      </span>
                      <span className="text-[#4a5045] text-sm leading-relaxed">{inc[lang]}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h2 className="font-serif text-3xl text-[#20261f] mb-6">{t.detail.pricing}</h2>
              <div className="bg-white rounded-2xl border border-[#eceae1] overflow-hidden">
                {activity.options.map((o, i) => (
                  <div
                    key={i}
                    className={`flex items-center justify-between px-6 py-4 ${
                      i !== activity.options.length - 1 || (activity.addons && activity.addons.length) ? "border-b border-[#f0eee5]" : ""
                    }`}
                  >
                    <span className="text-[#3d443a] font-medium">{o.label[lang]}</span>
                    <span className="font-serif text-xl text-[#20261f]">
                      {o.dhs} <span className="text-sm font-sans text-[#6b7166]">DHS · {o.eur}€</span>
                    </span>
                  </div>
                ))}
                {activity.addons && activity.addons.map((a, i) => (
                  <div
                    key={`addon-${i}`}
                    className={`flex items-center justify-between px-6 py-4 bg-[#f7f5ef] ${
                      i !== activity.addons.length - 1 ? "border-b border-[#f0eee5]" : ""
                    }`}
                  >
                    <span className="text-[#5c6b54] text-sm">+ {a.label[lang]}</span>
                    <span className="font-serif text-lg text-[#20261f]">
                      {a.dhs} <span className="text-sm font-sans text-[#6b7166]">DHS · {a.eur}€</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {activity.note && (
              <div className="flex items-start gap-3 bg-[#eef0e9] rounded-xl p-5">
                <Info size={20} className="text-[#5c6b54] shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs uppercase tracking-wide text-[#7c8f74] font-semibold mb-1">{t.detail.goodToKnow}</p>
                  <p className="text-[#4a5045] text-sm leading-relaxed">{activity.note[lang]}</p>
                </div>
              </div>
            )}
          </div>

          {/* Sticky booking card */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-28 bg-white rounded-2xl border border-[#eceae1] p-7 shadow-[0_18px_45px_-24px_rgba(28,34,30,0.3)]">
              <p className="text-xs tracking-[0.15em] uppercase text-[#9aa08f] mb-1">{t.activities.from}</p>
              <div className="flex items-end gap-2 mb-6">
                <span className="font-serif text-4xl text-[#20261f]">{activity.priceFromDhs}</span>
                <span className="text-[#6b7166] mb-1">DHS</span>
                <span className="text-[#9aa08f] mb-1 text-sm">≈ {activity.priceFromEur} €</span>
              </div>

              <div className="space-y-3 mb-6 text-sm">
                <div className="flex items-center gap-3 text-[#5a6055]">
                  <Clock size={16} className="text-[#7c8f74]" /> {activity.duration[lang]}
                </div>
                <div className="flex items-center gap-3 text-[#5a6055]">
                  <MapPin size={16} className="text-[#7c8f74]" /> {activity.location[lang]}
                </div>
              </div>

              <button
                onClick={() => openBooking(activity.id)}
                className="w-full bg-[#7c8f74] hover:bg-[#697c62] text-white rounded-full py-4 text-sm tracking-[0.1em] uppercase font-medium transition-all hover:-translate-y-0.5 hover:shadow-lg"
              >
                {t.detail.book}
              </button>

              <p className="mt-4 text-[11px] leading-relaxed text-[#9aa08f] text-center">
                {t.detail.preNote}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Suggestions */}
      <section className="pb-24 md:pb-28">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="flex items-end justify-between mb-10">
            <h2 className="font-serif text-3xl md:text-4xl text-[#20261f]">{t.detail.other}</h2>
            <Link to="/activites" className="hidden md:inline-flex items-center gap-2 text-sm tracking-[0.1em] uppercase font-medium text-[#5c6b54] hover:gap-3 transition-all">
              {t.activities.viewAll} <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7">
            {suggestions.map((a) => (
              <ActivityCard key={a.id} activity={a} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ActivityDetail;
